import FormDialog from '../../Dialog/FormDialog';
import {CollectionForm} from '../../Form/CollectionForm';
import {Collection} from '../../../types';
import {useFormSubmit} from '@alchemy/api';
import {clearWorkspaceCache, postCollection} from '../../../api/collection';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {StackedModalProps, useModals} from '@alchemy/navigation';
import {OnCollectionEdit} from '../../Dialog/Collection/EditCollection';
import React from 'react';
import {useDirtyFormPrompt} from '@alchemy/phrasea-framework';
import {WorkspaceChip} from '../../Ui/WorkspaceChip.tsx';
import {CollectionChip} from '../../Ui/CollectionChip.tsx';

type Props = {
    parent?: string;
    namePath?: string[];
    workspaceId?: string;
    workspaceName: string;
    onCreate?: OnCollectionEdit;
} & StackedModalProps;

export default function CreateCollection({
    parent,
    namePath,
    workspaceId,
    workspaceName,
    onCreate,
    ...modalProps
}: Props) {
    const {closeModal} = useModals();
    const {t} = useTranslation();

    const usedFormSubmit = useFormSubmit<Collection>({
        defaultValues: {
            name: '',
            privacy: 0,
        },
        onSubmit: async (data: Collection) => {
            return await postCollection({
                ...data,
                parent,
                workspace: workspaceId
                    ? `/workspaces/${workspaceId}`
                    : undefined,
            });
        },
        onSuccess: coll => {
            clearWorkspaceCache();
            toast.success(
                t(
                    'form.collection_create.success',
                    'Collection created!'
                ) as string
            );
            closeModal();
            onCreate?.(coll);
        },
    });

    const {submitting, remoteErrors, forbidNavigation} = usedFormSubmit;
    useDirtyFormPrompt(forbidNavigation, modalProps.modalIndex);
    const formId = 'create-collection';

    const title = namePath ? (
        <>
            {t(
                'form.collection_create.title_with_parent',
                'Create Collection under'
            )}{' '}
            <WorkspaceChip label={workspaceName} />
            {namePath.map((t, i) => (
                <React.Fragment key={i}>
                    {' / '}
                    <CollectionChip label={t} />
                </React.Fragment>
            ))}
        </>
    ) : (
        <>
            {t('form.collection_create.title', 'Create Collection in')}{' '}
            <WorkspaceChip label={workspaceName} />
        </>
    );

    return (
        <FormDialog
            {...modalProps}
            title={<div>{title}</div>}
            formId={formId}
            loading={submitting}
            errors={remoteErrors}
        >
            <CollectionForm formId={formId} usedFormSubmit={usedFormSubmit} />
        </FormDialog>
    );
}
